import React, { useEffect, useState } from "react";
import {
  Modal,
  Form,
  Input,
  Col,
  Row,
  message,
  Select,
  DatePicker,
  InputNumber,
  Table,
  Button,
} from "antd";
import {
  API_METHODS,
  BLANK_VALUE,
  DEFAULT_DATE_FORMAT,
} from "../../../constants";
import useApi from "../../../hooks/useApi";
import {
  formatCurrency,
  formatDateToDayJS,
  getClientConfig,
  getCreditors,
  isEmpty,
  replaceBlankValueToUndefined,
} from "../../../generalFunctions";
import dayjs from "dayjs";
import { RightOutlined, LeftOutlined } from "@ant-design/icons";

function AddDirectPurchase(props) {
  const [form] = Form.useForm();
  const { visible, close, selectedRecord, isViewOnly } = props;
  const [particularsList, setParticularsList] = useState([]);
  const columns = [
    {
      title: "Sr. No",
      dataIndex: "record",
      key: "record",
    },
    {
      title: "Particulars",
      dataIndex: "particulars",
      key: "particulars",
    },
    {
      title: "Amount",
      dataIndex: "amount",
      key: "amount",
      render: (text) => formatCurrency(text),
    },
    {
      title: "Action",
      key: "action",
      render: (text, record) => (
        <Button size="small" onClick={() => removeParticular(record)}>
          Remove
        </Button>
      ),
      isViewOnly: isViewOnly,
    },
  ].filter((c) => !c.isViewOnly);

  const [response, request] = useApi("/directPurchase", null, API_METHODS.POST);
  const [editResponse, editRequest] = useApi(
    `/directPurchase/${selectedRecord && selectedRecord.id}`,
    null,
    API_METHODS.PUT
  );
  const [singleGetResponse, singleGetRequest] = useApi(
    `/directPurchase/`,
    null,
    API_METHODS.GET
  );

  const { account } = getClientConfig();
  const [loadingSaveButton, setLoadingSaveButton] = useState(false);
  const [isDirectPurchaseEditable, setIsDirectPurchaseEditable] =
    useState(true);

  useEffect(() => {
    if (response.data) {
      close(true);
      setLoadingSaveButton(false);
      message.success("Direct Purchase added successfully.");
    }
  }, [response]);

  useEffect(() => {
    if (editResponse.data) {
      close(true);
      setLoadingSaveButton(false);
      message.success("Direct Purchase updated successfully.");
    }
  }, [editResponse]);

  useEffect(() => {
    if (selectedRecord) {
      let initialValues = { ...selectedRecord };
      if (isViewOnly === false && initialValues.isOnAccountPaymentDone === 1) {
        setIsDirectPurchaseEditable(false);
        message.error(
          "You can not edit the direct purchase.On account Payment is Done"
        );
      }
      initialValues = replaceBlankValueToUndefined(initialValues);
      initialValues.entryDate = formatDateToDayJS(initialValues.entryDate);
      initialValues.billDate = formatDateToDayJS(initialValues.billDate);
      setParticularsList(initialValues.particulars);
      initialValues.particulars = undefined;
      form.setFieldsValue(initialValues);
    }
  }, [selectedRecord]);

  useEffect(() => {
    if (
      singleGetResponse &&
      !isEmpty(singleGetResponse.data) &&
      !singleGetResponse.isLoading
    ) {
      const { data } = singleGetResponse;
      let initialValues = data[0];
      initialValues = replaceBlankValueToUndefined(initialValues);
      initialValues.entryDate = formatDateToDayJS(initialValues.entryDate);
      initialValues.billDate = formatDateToDayJS(initialValues.billDate);
      setParticularsList(initialValues.particulars);
      initialValues.particulars = undefined;
      form.setFieldsValue(initialValues);
    } else if (
      singleGetResponse &&
      singleGetResponse.data === BLANK_VALUE &&
      !singleGetResponse.isLoading
    ) {
      message.error("Direct Purchase not found");
    }
  }, [singleGetResponse]);

  const previousButtonClicked = () => {
    let id = form.getFieldValue("id") - 1;
    singleGetRequest(null, API_METHODS.GET, `/directPurchase/${id}`);
  };
  const nextButtonClicked = () => {
    let id = form.getFieldValue("id") + 1;
    singleGetRequest(null, API_METHODS.GET, `/directPurchase/${id}`);
  };

  const handleOk = () => {
    form.validateFields().then((values) => {
      if (!loadingSaveButton) {
        setLoadingSaveButton(true);
        if (values.entryDate) {
          values.entryDate = formatDateToDayJS(values.entryDate);
        }
        if (values.billDate) {
          values.billDate = formatDateToDayJS(values.billDate);
        }
        values.particulars = particularsList;
        delete values.amount;
        if (selectedRecord) {
          editRequest(values);
        } else {
          request(values, API_METHODS.POST);
        }
      }
    });
  };

  const handleCancel = () => {
    close();
  };

  const addParticulars = () => {
    const { particulars, amount, cgstRate, sgstRate, igstRate } =
      form.getFieldsValue();
    let totalAmount = 0;
    if (particulars && amount) {
      const list = [...particularsList];
      const record = {
        record: particularsList.length + 1,
        particulars,
        amount,
      };
      list.push(record);
      setParticularsList(list);
      for (let i = 0; i < particularsList.length; i++) {
        totalAmount = totalAmount + particularsList[i].amount;
      }
      totalAmount = totalAmount + amount;

      let cgst = Math.round(((totalAmount * cgstRate) / 100) * 1e2) / 1e2;
      let sgst = Math.round(((totalAmount * sgstRate) / 100) * 1e2) / 1e2;
      let igst = Math.round(((totalAmount * igstRate) / 100) * 1e2) / 1e2;

      let subTotal = (totalAmount + cgst + sgst + igst).toFixed(2);

      let roundOff = Math.round((Math.round(subTotal) - subTotal) * 1e2) / 1e2;

      form.setFieldsValue({
        particulars: undefined,
        amount: undefined,
        totalAmount,
        cgst,
        sgst,
        igst,
        subTotal,
        roundOff,
        billAmount: Math.round(subTotal),
      });
    }
  };

  const removeParticular = (record) => {
    const { totalAmount, cgstRate, sgstRate, igstRate } = form.getFieldsValue();
    let newAmount = 0;
    let list = [...particularsList];
    const index = list.findIndex((r) => r.record == record.record);
    if (index >= 0) {
      list.splice(index, 1);

      newAmount = totalAmount - record.amount;

      let cgst = Math.round(((newAmount * cgstRate) / 100) * 1e2) / 1e2;
      let sgst = Math.round(((newAmount * sgstRate) / 100) * 1e2) / 1e2;
      let igst = Math.round(((newAmount * igstRate) / 100) * 1e2) / 1e2;

      let subTotal = (newAmount + cgst + sgst + igst).toFixed(2);

      let roundOff = Math.round((Math.round(subTotal) - subTotal) * 1e2) / 1e2;

      setParticularsList(list);
      form.setFieldsValue({
        totalAmount: newAmount,
        cgst,
        sgst,
        igst,
        subTotal,
        roundOff,
        billAmount: Math.round(subTotal),
      });
    }
  };

  const cgstRateChanged = () => {
    const { totalAmount, cgstRate, sgst, igst } = form.getFieldsValue();
    let cgst = Math.round(((totalAmount * cgstRate) / 100) * 1e2) / 1e2;
    let subTotal = (totalAmount + cgst + sgst + igst).toFixed(2);
    let roundOff = Math.round((Math.round(subTotal) - subTotal) * 1e2) / 1e2;

    form.setFieldsValue({
      cgst,
      roundOff,
      subTotal,
      billAmount: Math.round(subTotal),
    });
  };
  const sgstRateChanged = () => {
    const { totalAmount, sgstRate, cgst, igst } = form.getFieldsValue();
    let sgst = Math.round(((totalAmount * sgstRate) / 100) * 1e2) / 1e2;
    let subTotal = (totalAmount + cgst + sgst + igst).toFixed(2);
    let roundOff = Math.round((Math.round(subTotal) - subTotal) * 1e2) / 1e2;

    form.setFieldsValue({
      sgst,
      roundOff,
      subTotal,
      billAmount: Math.round(subTotal),
    });
  };
  const igstRateChanged = () => {
    const { totalAmount, igstRate, cgst, sgst } = form.getFieldsValue();
    let igst = Math.round(((totalAmount * igstRate) / 100) * 1e2) / 1e2;
    let subTotal = (totalAmount + cgst + sgst + igst).toFixed(2);
    let roundOff = Math.round((Math.round(subTotal) - subTotal) * 1e2) / 1e2;

    form.setFieldsValue({
      igst,
      roundOff,
      subTotal,
      billAmount: Math.round(subTotal),
    });
  };

  const isFormDisabled = () => {
    let disabled = true;
    if (isDirectPurchaseEditable && !isViewOnly) {
      disabled = false;
    }
    return disabled;
  };

  return (
    <Modal
      title="Add Direct Purchase"
      open={visible}
      onOk={handleOk}
      onCancel={handleCancel}
      width={1000}
      okText="Save"
      cancelText={isViewOnly ? "Close" : "Cancel"}
      okButtonProps={{
        disabled: isViewOnly ? true : false,
        loading: loadingSaveButton,
      }}
    >
      <Form
        layout="vertical"
        form={form}
        disabled={isFormDisabled()}
        initialValues={{
          entryDate: dayjs(),
          billDate: dayjs(),
          cgstRate: 2.5,
          sgstRate: 2.5,
          igstRate: 0,
          igst: 0,
        }}
      >
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="entryDate"
              label="Entry Date"
              rules={[{ required: true, message: "Entry Date is required." }]}
            >
              <DatePicker format={DEFAULT_DATE_FORMAT} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="billNumber"
              label="Bill Number"
              rules={[{ required: true, message: "Bill Number is required." }]}
            >
              <Input />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="billDate"
              label="Bill Date"
              rules={[{ required: true, message: "Bill Date is required." }]}
            >
              <DatePicker format={DEFAULT_DATE_FORMAT} />
            </Form.Item>
          </Col>
          <Col span={8}>
            <Form.Item
              name="creditorId"
              label="Creditor"
              rules={[{ required: true, message: "Creditor is required." }]}
            >
              <Select
                allowClear
                showSearch
                filterOption={(input, option) =>
                  option.children.toLowerCase().indexOf(input.toLowerCase()) >=
                  0
                }
              >
                {getCreditors(account).map((option) => (
                  <Select.Option key={option.id} value={option.id}>
                    {option.accountName}
                  </Select.Option>
                ))}
              </Select>
            </Form.Item>
          </Col>
        </Row>
        {!isViewOnly && (
          <Row gutter={16}>
            <Col span={14}>
              <Form.Item
                name="particulars"
                label="Particulars"
                rules={[
                  {
                    max: 500,
                    message: "Particulars can be maximum of 500 characters.",
                  },
                ]}
              >
                <Input.TextArea showCount maxLength={500} />
              </Form.Item>
            </Col>
            <Col span={7}>
              <Form.Item name="amount" label="Amount">
                <InputNumber />
              </Form.Item>
            </Col>
            <Col span={3}>
              <Form.Item label=" ">
                <Button type="default" onClick={addParticulars}>
                  Add Record
                </Button>
              </Form.Item>
            </Col>
          </Row>
        )}

        <Row style={{ paddingTop: 20 }}>
          <Col span={24}>
            <Table
              rowKey="record"
              size="small"
              pagination={false}
              columns={columns}
              dataSource={particularsList}
            />
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item
              name="narration"
              label="Narration"
              rules={[
                { required: true, message: "Narration is required." },
                {
                  max: 100,
                  message: "Narration should be maximum of 100 characters.",
                },
              ]}
            >
              <Input />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={8}>
            <Form.Item name="totalAmount" label="Total Amount">
              <InputNumber disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item name="cgstRate" label="CGST %">
              <InputNumber onChange={() => cgstRateChanged()} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="cgst" label="CGST Rs.">
              <InputNumber disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item name="sgstRate" label="SGST %">
              <InputNumber onChange={() => sgstRateChanged()} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="sgst" label="SGST Rs.">
              <InputNumber disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item name="igstRate" label="IGST %">
              <InputNumber onChange={() => igstRateChanged()} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="igst" label="IGST Rs.">
              <InputNumber disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item name="subTotal" label="Sub Total">
              <InputNumber disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item name="roundOff" label="Round Off">
              <InputNumber disabled={true} />
            </Form.Item>
          </Col>
        </Row>
        <Row gutter={16}>
          <Col span={4}>
            <Form.Item name="billAmount" label="Bill Amount">
              <InputNumber disabled={true} />
            </Form.Item>
          </Col>
        </Row>
      </Form>
      {isViewOnly && (
        <Row justify="space-between">
          <Button type="primary" onClick={previousButtonClicked}>
            <LeftOutlined />
            Previous
          </Button>
          <Button type="primary" onClick={nextButtonClicked}>
            Next
            <RightOutlined />
          </Button>
        </Row>
      )}
    </Modal>
  );
}

export default AddDirectPurchase;
